/**
 * Stellungnahmen-Modul — Workflow „Nach Stellungnahme → Rechnung"
 * Issue #242 (Teil von #239).
 *
 * Setzt die Status-Kette
 *
 *   Stellungnahme in Arbeit  →  Stellungnahme versendet
 *   →  Rechnung offen  →  Rechnung bezahlt
 *
 * Beim Übergang auf „Stellungnahme versendet" wird automatisch eine
 * Folge-Aufgabe „Rechnung für Stellungnahme erstellen" angelegt, die über
 * `eltern_aufgabe_id` mit der ursprünglichen Stellungnahmen-Aufgabe verknüpft
 * ist. Das Honorar wird aus der honorartabelle (leistungstyp='stellungnahme'
 * bzw. 'ergaenzungsgutachten') abgerufen und im Rechnungs-Template vorbelegt;
 * die Beschreibung enthält den Bezug auf AG-Aktenzeichen und den zugehörigen
 * Schriftsatz.
 */

import { supabase } from './supabase';
import type { AufgabenStatus, RechnungsStatus } from './supabase';

// ── Workflow-Status ─────────────────────────────────────────────────────────
export type StellungnahmeWorkflowStatus =
  | 'stellungnahme_in_arbeit'
  | 'stellungnahme_versendet'
  | 'rechnung_offen'
  | 'rechnung_bezahlt';

export const STELLUNGNAHME_WORKFLOW_STATUS_LABELS: Record<
  StellungnahmeWorkflowStatus,
  string
> = {
  stellungnahme_in_arbeit: 'Stellungnahme in Arbeit',
  stellungnahme_versendet: 'Stellungnahme versendet',
  rechnung_offen: 'Rechnung offen',
  rechnung_bezahlt: 'Rechnung bezahlt',
};

export const STELLUNGNAHME_WORKFLOW_REIHENFOLGE: readonly StellungnahmeWorkflowStatus[] = [
  'stellungnahme_in_arbeit',
  'stellungnahme_versendet',
  'rechnung_offen',
  'rechnung_bezahlt',
] as const;

/**
 * 1-basierter Schritt-Index (1..4) für den übergebenen Status. Für Progress-
 * Indikatoren im UI. Unbekannte Werte → 0.
 */
export function workflowSchrittNummer(status: StellungnahmeWorkflowStatus): number {
  return STELLUNGNAHME_WORKFLOW_REIHENFOLGE.indexOf(status) + 1;
}

export type Leistungstyp = 'gutachten' | 'stellungnahme' | 'ergaenzungsgutachten';

/** Kategorie-Wert, den eine Stellungnahme-Aufgabe in `aufgaben.kategorie` trägt. */
export const STELLUNGNAHME_AUFGABE_KATEGORIE = 'stellungnahme' as const;

// ── Status ableiten ─────────────────────────────────────────────────────────
export function ermittleWorkflowStatus(params: {
  stellungnahmeAufgabeStatus: AufgabenStatus | string;
  rechnung?: { status: RechnungsStatus | string } | null;
}): StellungnahmeWorkflowStatus {
  if (params.rechnung?.status === 'bezahlt') return 'rechnung_bezahlt';
  if (params.rechnung) return 'rechnung_offen';
  if (params.stellungnahmeAufgabeStatus === 'erledigt') return 'stellungnahme_versendet';
  return 'stellungnahme_in_arbeit';
}

// ── Honorar aus honorartabelle laden ────────────────────────────────────────
export interface StellungnahmeHonorar {
  grundhonorar: number;
  schreibkosten: number;
  porto_telefon: number;
  gesamt: number;
}

export async function ladeStellungnahmeHonorar(
  leistungstyp: Leistungstyp = 'stellungnahme',
): Promise<StellungnahmeHonorar | null> {
  const { data, error } = await supabase
    .from('honorartabelle')
    .select('grundhonorar, schreibkosten, porto_telefon')
    .eq('aktiv', true)
    .eq('leistungstyp', leistungstyp)
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  const grundhonorar = Number(data.grundhonorar ?? 0);
  const schreibkosten = Number(data.schreibkosten ?? 0);
  const porto_telefon = Number(data.porto_telefon ?? 0);
  const gesamt = grundhonorar + schreibkosten + porto_telefon;

  return { grundhonorar, schreibkosten, porto_telefon, gesamt };
}

// ── Rechnungs-Template (Beschreibung + Betrag) ──────────────────────────────
export interface RechnungsTemplateInput {
  anwaltAktenzeichen: string | null;
  schriftsatzBezeichnung: string | null;
  stellungnahmeTitel: string;
  honorarGesamt: number;
}

export interface RechnungsTemplate {
  titel: string;
  beschreibung: string;
  betrag: number;
}

export function erzeugeRechnungsTemplate(input: RechnungsTemplateInput): RechnungsTemplate {
  const azZeile = input.anwaltAktenzeichen
    ? `AG-Aktenzeichen: ${input.anwaltAktenzeichen}`
    : 'AG-Aktenzeichen: (nicht gesetzt)';
  const schriftsatzZeile = input.schriftsatzBezeichnung
    ? `Schriftsatz: ${input.schriftsatzBezeichnung}`
    : `Schriftsatz: ${input.stellungnahmeTitel}`;
  const betrag = Math.round(input.honorarGesamt * 100) / 100;

  const beschreibung = [
    'Rechnung für die versendete Stellungnahme.',
    azZeile,
    schriftsatzZeile,
    `Honorar (Netto): ${betrag.toFixed(2)} €`,
  ].join('\n');

  return {
    titel: 'Rechnung für Stellungnahme erstellen',
    beschreibung,
    betrag,
  };
}

// ── Auto-Erzeugung der Rechnungsaufgabe beim Versand ────────────────────────
export interface StellungnahmeAufgabeMinimal {
  id: string;
  business_unit: string;
  akte_id: string | null;
  titel: string;
  /**
   * Optional. Wird — falls gesetzt — gegen `STELLUNGNAHME_AUFGABE_KATEGORIE`
   * geprüft, damit der Trigger nicht versehentlich für Aufgaben anderer
   * Kategorien feuert. Fehlt das Feld (Legacy-Aufrufer), wird die Prüfung
   * übersprungen; der Caller muss die Kategorie dann selbst sicherstellen.
   */
  kategorie?: string | null;
}

export interface ErzeugeRechnungsaufgabeInput {
  stellungnahmeAufgabe: StellungnahmeAufgabeMinimal;
  anwaltAktenzeichen: string | null;
  schriftsatzBezeichnung?: string | null;
  leistungstyp?: Leistungstyp;
}

export async function erzeugeRechnungsaufgabeAusStellungnahme(
  input: ErzeugeRechnungsaufgabeInput,
): Promise<{ id: string; titel: string; beschreibung: string } | null> {
  const { stellungnahmeAufgabe } = input;
  if (!stellungnahmeAufgabe.akte_id) return null;
  if (
    stellungnahmeAufgabe.kategorie != null &&
    stellungnahmeAufgabe.kategorie !== STELLUNGNAHME_AUFGABE_KATEGORIE
  ) {
    return null;
  }

  const honorar =
    (await ladeStellungnahmeHonorar(input.leistungstyp ?? 'stellungnahme')) ?? {
      grundhonorar: 0,
      schreibkosten: 0,
      porto_telefon: 0,
      gesamt: 0,
    };

  const template = erzeugeRechnungsTemplate({
    anwaltAktenzeichen: input.anwaltAktenzeichen,
    schriftsatzBezeichnung: input.schriftsatzBezeichnung ?? null,
    stellungnahmeTitel: stellungnahmeAufgabe.titel,
    honorarGesamt: honorar.gesamt,
  });

  const { data, error } = await supabase
    .from('aufgaben')
    .insert({
      business_unit: stellungnahmeAufgabe.business_unit,
      akte_id: stellungnahmeAufgabe.akte_id,
      eltern_aufgabe_id: stellungnahmeAufgabe.id,
      kategorie: 'rechnung',
      status: 'offen',
      prioritaet: 'mittel',
      titel: template.titel,
      beschreibung: template.beschreibung,
    })
    .select()
    .single();

  if (error || !data) return null;
  return { id: data.id, titel: data.titel, beschreibung: data.beschreibung };
}

// ── Trigger-Helper: wird vom UI/MCP aufgerufen wenn die Stellungnahme
//     auf „erledigt" (= versendet) gesetzt wird ───────────────────────────
export async function triggerRechnungNachStellungnahmeVersand(params: {
  stellungnahmeAufgabe: StellungnahmeAufgabeMinimal;
  neuerStatus: AufgabenStatus | string;
  anwaltAktenzeichen: string | null;
  schriftsatzBezeichnung?: string | null;
  leistungstyp?: Leistungstyp;
}): Promise<{ id: string; titel: string; beschreibung: string } | null> {
  if (params.neuerStatus !== 'erledigt') return null;
  // Kategorie-Guard: nur Stellungnahmen-Aufgaben sollen einen Rechnungs-
  // Folgeschritt auslösen. Wenn die Kategorie explizit gesetzt und ≠
  // 'stellungnahme' ist, still abbrechen.
  if (
    params.stellungnahmeAufgabe.kategorie != null &&
    params.stellungnahmeAufgabe.kategorie !== STELLUNGNAHME_AUFGABE_KATEGORIE
  ) {
    return null;
  }
  return erzeugeRechnungsaufgabeAusStellungnahme({
    stellungnahmeAufgabe: params.stellungnahmeAufgabe,
    anwaltAktenzeichen: params.anwaltAktenzeichen,
    schriftsatzBezeichnung: params.schriftsatzBezeichnung,
    leistungstyp: params.leistungstyp,
  });
}

// ── Integrations-Helper: Akte nachladen + triggern ──────────────────────
/**
 * Vollständige Integration für den UI-Layer (z. B. useAufgabenCRUD):
 * lädt das AG-Aktenzeichen aus der zugehörigen Akte nach und ruft
 * `triggerRechnungNachStellungnahmeVersand` auf. Wirft nicht — Fehler
 * werden als `null` zurückgegeben, damit der umgebende Update-Flow nicht
 * reißen kann.
 */
export async function fuehreStellungnahmeRechnungsTriggerAus(params: {
  stellungnahmeAufgabe: StellungnahmeAufgabeMinimal;
  neuerStatus: AufgabenStatus | string;
  leistungstyp?: Leistungstyp;
}): Promise<{ id: string; titel: string; beschreibung: string } | null> {
  if (params.neuerStatus !== 'erledigt') return null;
  if (
    params.stellungnahmeAufgabe.kategorie != null &&
    params.stellungnahmeAufgabe.kategorie !== STELLUNGNAHME_AUFGABE_KATEGORIE
  ) {
    return null;
  }

  let anwaltAktenzeichen: string | null = null;
  if (params.stellungnahmeAufgabe.akte_id) {
    try {
      const { data } = await supabase
        .from('akten')
        .select('anwalt_aktenzeichen')
        .eq('id', params.stellungnahmeAufgabe.akte_id)
        .maybeSingle();
      anwaltAktenzeichen = (data?.anwalt_aktenzeichen as string | null) ?? null;
    } catch {
      anwaltAktenzeichen = null;
    }
  }

  return triggerRechnungNachStellungnahmeVersand({
    stellungnahmeAufgabe: params.stellungnahmeAufgabe,
    neuerStatus: params.neuerStatus,
    anwaltAktenzeichen,
    schriftsatzBezeichnung: params.stellungnahmeAufgabe.titel,
    leistungstyp: params.leistungstyp,
  });
}
